import { checkpointState } from '@ofmos/shared/checklist';
import { condition, type FilterNode } from '@ofmos/shared/filter-ast';
import type { OrderBy } from '@ofmos/shared/record-query';
import { scopeFor } from '@ofmos/shared/roles';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../../data/client';
import { useFields, useHealthConfigs, useObjects, useRecordList } from '../../../data/hooks';
import { queryKeys } from '../../../data/queryKeys';
import { optionById } from '../../../fields/fieldTypeMeta';
import { useSession } from '../../auth/useSession';
import { fieldsByName, objectByName } from '../../dashboard/queries';
import { relationLabelOf, textOf } from '../../dashboard/rowValues';
import { toCheckpointField } from '../../record/checklist';
import { isMoney } from '../clients/clientModel';
import { type IntakesToReviewCount, useIntakesToReviewCount } from '../intake/useIntakesToReview';
import { FULFILLMENT } from '../shared';
import {
  type AgencyFacts,
  type EnrollmentFacts,
  type HealthColour,
  NO_REASON_RULES,
  type PaymentFacts,
  type ReasonRules,
  reasonRulesOf,
  type StepFacts,
} from './model';

/**
 * WHAT THE DASHBOARD READS, BY API NAME (pages resolve objects and fields BY API NAME against
 * runtime metadata). An object or a field this installation does not have makes its part of
 * the page ABSENT - an empty select is a query that never runs - and never a crash.
 *
 * FIVE LIST READS AND ONE COUNT, whatever the size of the client base: every agency, every
 * enrollment, the unpaid payments and the deals behind them (for whoever may read both), the
 * health config the reason sentences came from, and the number of intakes waiting for review.
 * The joins happen in `model.ts`.
 */

/** The configured onboarding steps' field names, in order (nine in OFMOS, register D5). */
export const STEP_FIELD_NAMES: readonly string[] = FULFILLMENT.onboardingSteps.map(
  (step) => step.field,
);

/** One page holds the whole client base: the query ceiling is 500 rows. */
const WHOLE_LIST = 500;
const NO_FILTER: FilterNode = { kind: 'group', combinator: 'and', children: [] };

const AGENCY_FIELDS = [
  'name',
  'csm',
  'last_client_message_at',
  'unanswered_since',
  'unanswered_excerpt',
  'reply_handled_at',
  'paused_until',
  'lifecycle_status',
  'churned_at',
] as const;

const ENROLLMENT_FIELDS = [
  'name',
  'agency',
  'enrollment_status',
  'phase',
  'tier',
  'health',
  'health_reason',
  'health_since',
  'contract_start_date',
] as const;

const PAYMENT_FIELDS = [
  'deal',
  'amount',
  'due_date',
  'paid_at',
  'payment_status',
  'promised_at',
  'promise_note',
] as const;

export interface DashboardData {
  readonly isPending: boolean;
  /** The metadata is in, so `resolves` is an answer and not a guess. */
  readonly modelKnown: boolean;
  /** The page's model resolves: an agency object and enrollments carrying `health` (H1). */
  readonly resolves: boolean;
  readonly agencyObject: ObjectMetadata | undefined;
  readonly agencyFields: ReadonlyMap<string, FieldMetadata>;
  readonly agencies: ReadonlyMap<string, AgencyFacts>;
  readonly enrollments: readonly EnrollmentFacts[];
  /** The enrollment has `enrollment_status`; without it any enrollment is active. */
  readonly hasStatusField: boolean;
  readonly reasonRules: ReasonRules;
  readonly paymentObject: ObjectMetadata | undefined;
  /** The payment object and the fields Money reads exist here; otherwise Money is absent. */
  readonly moneyResolves: boolean;
  /** False for a person who may not read payments or deals: the Money tile shows its em dash. */
  readonly canReadMoney: boolean;
  /** Unpaid payments, joined to their client; empty while unreadable. */
  readonly payments: readonly PaymentFacts[];
  /**
   * The deliveries nobody has marked as reviewed, from the one count the Onboarding button reads
   * (`useIntakesToReviewCount`, BE-INR: "Reviewed at" empty), so both pages say one number.
   */
  readonly intakeNeedsReview: IntakesToReviewCount;
}

export function useDashboardData(): DashboardData {
  const objects = useObjects();
  const session = useSession();
  const agencyObject = objectByName(objects.data, 'agency');
  const enrollmentObject = objectByName(objects.data, 'cohort_enrollment');
  const paymentObject = objectByName(objects.data, 'payment');
  const dealObject = objectByName(objects.data, 'deal');

  const agencyFields = useFields(agencyObject?.id ?? '', agencyObject !== undefined);
  const enrollmentFields = useFields(enrollmentObject?.id ?? '', enrollmentObject !== undefined);
  const paymentFields = useFields(paymentObject?.id ?? '', paymentObject !== undefined);
  const dealFields = useFields(dealObject?.id ?? '', dealObject !== undefined);
  const agencyByName = useMemo(() => fieldsByName(agencyFields.data), [agencyFields.data]);
  const enrollmentByName = useMemo(
    () => fieldsByName(enrollmentFields.data),
    [enrollmentFields.data],
  );
  const paymentByName = useMemo(() => fieldsByName(paymentFields.data), [paymentFields.data]);
  const dealByName = useMemo(() => fieldsByName(dealFields.data), [dealFields.data]);

  const agencyRows = useRows(agencyObject?.id, idsOf(agencyByName, AGENCY_FIELDS, 'name'));

  const stepFields = useMemo(
    () =>
      STEP_FIELD_NAMES.map((name) => enrollmentByName.get(name)).filter(
        (field): field is FieldMetadata => field !== undefined,
      ),
    [enrollmentByName],
  );
  const enrollmentSelect = useMemo(() => {
    const base = idsOf(enrollmentByName, ENROLLMENT_FIELDS, 'agency');
    if (base.length === 0) return base;
    /** A deadline counts from its base field, so the base has to be in the row too. */
    const bases = stepFields.flatMap((field) => {
      const checkpoint = field.options?.checkpoint;
      return [checkpoint?.baseFieldId, checkpoint?.blockedByFieldId].filter(
        (id): id is string => typeof id === 'string',
      );
    });
    return [...new Set([...base, ...stepFields.map((field) => field.id), ...bases])];
  }, [enrollmentByName, stepFields]);
  const enrollmentRows = useRows(enrollmentObject?.id, enrollmentSelect);

  /**
   * MONEY ONLY FOR WHOEVER MAY READ PAYMENTS AND DEALS (the client page's rule): a payment
   * names its client through the deal's agency, two hops, so both lists are read and joined.
   */
  const permissions = session.data?.permissions;
  const mayRead = (object: ObjectMetadata | undefined) =>
    object !== undefined &&
    (permissions === undefined || scopeFor(permissions, object.id, 'read') !== 'none');
  const moneyResolves =
    paymentObject !== undefined &&
    paymentByName.has('amount') &&
    paymentByName.has('due_date') &&
    paymentByName.has('deal');
  const canReadMoney =
    moneyResolves && mayRead(paymentObject) && mayRead(dealObject) && dealByName.has('agency');
  const paidAt = paymentByName.get('paid_at');
  const paymentRows = useRows(
    canReadMoney ? paymentObject?.id : undefined,
    idsOf(paymentByName, PAYMENT_FIELDS, 'amount'),
    paidAt === undefined ? {} : { filter: condition(paidAt.id, 'isEmpty') },
  );
  const dealAgency = dealByName.get('agency');
  const dealRows = useRows(
    canReadMoney ? dealObject?.id : undefined,
    dealAgency === undefined ? [] : [dealAgency.id],
  );

  const healthConfigs = useHealthConfigs();
  const intake = useIntakesToReviewCount();

  const agencies = useMemo(() => {
    const map = new Map<string, AgencyFacts>();
    for (const row of agencyRows.rows) map.set(row.id, agencyFactsOf(row, agencyByName));
    return map;
  }, [agencyRows.rows, agencyByName]);

  const enrollments = useMemo(
    () => enrollmentRows.rows.map((row) => enrollmentFactsOf(row, enrollmentByName, stepFields)),
    [enrollmentRows.rows, enrollmentByName, stepFields],
  );

  const reasonRules = useMemo(() => {
    const config = healthConfigs.data?.find(
      (entry) => entry.config.objectId === enrollmentObject?.id,
    )?.config;
    return config === undefined
      ? NO_REASON_RULES
      : reasonRulesOf(config, {
          lastClientMessageAt: agencyByName.get('last_client_message_at')?.id,
          unansweredSince: agencyByName.get('unanswered_since')?.id,
        });
  }, [healthConfigs.data, enrollmentObject?.id, agencyByName]);

  const payments = useMemo(() => {
    if (!canReadMoney) return [];
    const agencyOfDeal = new Map<string, string>();
    for (const row of dealRows.rows) {
      const agencyId = dealAgency === undefined ? undefined : row.values[dealAgency.id];
      if (typeof agencyId === 'string' && agencyId !== '') agencyOfDeal.set(row.id, agencyId);
    }
    return paymentRows.rows.map((row) =>
      paymentFactsOf(row, paymentByName, agencyOfDeal, agencies),
    );
  }, [canReadMoney, dealRows.rows, dealAgency, paymentRows.rows, paymentByName, agencies]);

  return {
    /**
     * Everything a count on the screen reads: the lists, the rules that sort the red clients
     * (else a Gone quiet row would sit under Needs attention for a moment), and the money.
     */
    isPending:
      objects.isPending ||
      (agencyObject !== undefined && (agencyFields.isPending || agencyRows.isPending)) ||
      (enrollmentObject !== undefined &&
        (enrollmentFields.isPending || enrollmentRows.isPending)) ||
      healthConfigs.isPending ||
      (canReadMoney && (paymentRows.isPending || dealRows.isPending)),
    modelKnown:
      !objects.isPending &&
      (enrollmentObject === undefined || !enrollmentFields.isPending) &&
      (agencyObject === undefined || !agencyFields.isPending),
    resolves:
      agencyObject !== undefined &&
      enrollmentObject !== undefined &&
      enrollmentByName.has('health'),
    agencyObject,
    agencyFields: agencyByName,
    agencies,
    enrollments,
    hasStatusField: enrollmentByName.has('enrollment_status'),
    reasonRules,
    paymentObject,
    moneyResolves,
    canReadMoney,
    payments,
    intakeNeedsReview: intake,
  };
}

/**
 * The ids of the named fields, or NOTHING when the one field the rows cannot live without is
 * missing - an empty select disables the read, which is the absent state.
 */
function idsOf(
  fields: ReadonlyMap<string, FieldMetadata>,
  names: readonly string[],
  required: string,
): readonly string[] {
  if (!fields.has(required)) return [];
  return names.map((name) => fields.get(name)?.id).filter((id): id is string => id !== undefined);
}

/**
 * A WHOLE LIST AS ROWS. `useRecordList` caches ids and fans the rows into their canonical
 * entries (INV-13); the rows are read back from those entries rather than through one
 * `useRecord` per id, because two hundred observers would each refetch once the entries go
 * stale. The list refetches as a whole, and the rows follow it.
 *
 * AN OPTIMISTIC WRITE TO ONE ENTRY IS FOLLOWED TOO ("Mark as handled", a pause: D2 "the
 * page hides the row at once"). It changes neither the id list nor its time, so the rows also
 * re-read whenever a canonical entry of this object changes in the cache.
 */
function useRows(
  objectId: string | undefined,
  select: readonly string[],
  extra: {
    readonly filter?: FilterNode;
    readonly orderBy?: readonly OrderBy[];
    readonly limit?: number;
  } = {},
): { readonly rows: readonly RecordRow[]; readonly isPending: boolean } {
  const queryClient = useQueryClient();
  const enabled = objectId !== undefined && select.length > 0;
  const list = useRecordList(objectId ?? '', {
    select,
    filter: extra.filter ?? NO_FILTER,
    orderBy: extra.orderBy ?? [],
    limit: extra.limit ?? WHOLE_LIST,
    enabled,
  });
  const ids = list.data?.ids;
  const updatedAt = list.dataUpdatedAt;
  const version = useCanonicalVersion(enabled ? objectId : undefined);
  const rows = useMemo(() => {
    void updatedAt;
    void version;
    if (objectId === undefined || ids === undefined) return [];
    return ids
      .map((id) => queryClient.getQueryData<RecordRow | null>(queryKeys.record(objectId, id)))
      .filter((row): row is RecordRow => row !== undefined && row !== null);
  }, [ids, updatedAt, version, objectId, queryClient]);
  return { rows, isPending: enabled && list.isPending };
}

/** A counter that moves whenever a canonical record entry of `objectId` gets new data. */
function useCanonicalVersion(objectId: string | undefined): number {
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (objectId === undefined) return;
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      const key = event.query.queryKey;
      if (key[0] === 'record' && key[1] === objectId) setVersion((value) => value + 1);
    });
  }, [objectId, queryClient]);
  return version;
}

function optionLabel(row: RecordRow, field: FieldMetadata | undefined): string | null {
  if (field === undefined) return null;
  const value = row.values[field.id];
  if (typeof value !== 'string' || value === '') return null;
  return optionById(field, value)?.label ?? null;
}

function agencyFactsOf(row: RecordRow, fields: ReadonlyMap<string, FieldMetadata>): AgencyFacts {
  const id = (name: string) => fields.get(name)?.id;
  const csm = id('csm');
  const csmId = csm === undefined ? null : row.values[csm];
  return {
    id: row.id,
    name: textOf(row, id('name')) ?? 'Untitled',
    csmId: typeof csmId === 'string' && csmId !== '' ? csmId : null,
    csmName: relationLabelOf(row, csm),
    lastClientMessageAt: textOf(row, id('last_client_message_at')),
    unansweredSince: textOf(row, id('unanswered_since')),
    unansweredExcerpt: textOf(row, id('unanswered_excerpt')),
    replyHandledAt: textOf(row, id('reply_handled_at')),
    pausedUntil: textOf(row, id('paused_until')),
    lifecycle: optionLabel(row, fields.get('lifecycle_status')),
    churnedAt: textOf(row, id('churned_at')),
  };
}

function enrollmentFactsOf(
  row: RecordRow,
  fields: ReadonlyMap<string, FieldMetadata>,
  stepFields: readonly FieldMetadata[],
): EnrollmentFacts {
  const id = (name: string) => fields.get(name)?.id;
  const agency = id('agency');
  const agencyId = agency === undefined ? null : row.values[agency];
  const health = optionLabel(row, fields.get('health'));
  const steps: StepFacts[] = stepFields.map((field) => {
    const state = checkpointState(toCheckpointField(field), row.values);
    const late = state.status === 'late';
    return {
      name: field.name,
      label: field.label,
      // A waived step (the onboarding call already happened, R2-C) is finished for the board.
      done: state.status === 'done' || state.status === 'waived',
      late,
      daysLate: late && state.days !== null ? -state.days : null,
    };
  });
  return {
    id: row.id,
    agencyId: typeof agencyId === 'string' && agencyId !== '' ? agencyId : null,
    agencyLabel: relationLabelOf(row, agency) ?? textOf(row, id('name')),
    status: optionLabel(row, fields.get('enrollment_status')),
    phase: optionLabel(row, fields.get('phase')),
    tier: optionLabel(row, fields.get('tier')),
    health:
      health === 'Red' || health === 'Yellow' || health === 'Green'
        ? (health as HealthColour)
        : null,
    healthReason: textOf(row, id('health_reason')),
    healthSince: textOf(row, id('health_since')),
    contractStartDate: textOf(row, id('contract_start_date')),
    steps,
  };
}

function paymentFactsOf(
  row: RecordRow,
  fields: ReadonlyMap<string, FieldMetadata>,
  agencyOfDeal: ReadonlyMap<string, string>,
  agencies: ReadonlyMap<string, AgencyFacts>,
): PaymentFacts {
  const id = (name: string) => fields.get(name)?.id;
  const deal = id('deal');
  const dealId = deal === undefined ? null : row.values[deal];
  const agencyId = typeof dealId === 'string' ? (agencyOfDeal.get(dealId) ?? null) : null;
  const amountId = id('amount');
  const amount = amountId === undefined ? undefined : row.values[amountId];
  return {
    id: row.id,
    agencyId,
    clientName:
      (agencyId === null ? undefined : agencies.get(agencyId)?.name) ?? relationLabelOf(row, deal),
    amount: isMoney(amount) ? amount : null,
    dueDate: textOf(row, id('due_date')),
    paidAt: textOf(row, id('paid_at')),
    status: optionLabel(row, fields.get('payment_status')),
    promisedAt: textOf(row, id('promised_at')),
    promiseNote: textOf(row, id('promise_note')),
  };
}
